import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, computed, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatStepperModule } from '@angular/material/stepper';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { StepperSelectionEvent } from '@angular/cdk/stepper';

import {
  STEP_LABELS,
  STEP_ORDER,
  STEP_PATH,
  StepKey,
  StepState,
  StepStatus,
} from '../../model/client-onboarding/onboarding-wizard-stepper';

import { HeaderComponent } from '../header/header.component';
import { LanguageSelectorComponent } from '../language-selector/language-selector.component';
import { ProfileMenuComponent } from '../profile-menu/profile-menu.component';
import { UtilitiesService } from '../../services/utilities.service';
import { normalizeFromApi } from '../../model/client-onboarding/onboarding-wizard-stepper-helper';
import { OnboardingWizardService } from '../../services/onboarding-wizard/onboarding-wizard.service';
import { distinctUntilChanged, filter, map, startWith, Subject, switchMap, take, takeUntil, tap } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatCardModule } from '@angular/material/card';
import { ScopeService } from '../../services/scope.service';

@Component({
  selector: 'app-onboarding-wizard',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    MatStepperModule,
    MatButtonModule,
    MatIconModule,
    MatExpansionModule,
    MatToolbarModule,
    MatTooltipModule,
    HeaderComponent,
    ProfileMenuComponent,
    LanguageSelectorComponent,
    MatCardModule
  ],
  templateUrl: './onboarding-wizard.component.html',
  styleUrl: './onboarding-wizard.component.scss',
})
export class OnboardingWizardComponent implements OnInit, OnDestroy {

  private STORAGE_KEY = 'fd_onboarding_sidebar_collapsed';

  collapsed = signal<boolean>(this.readCollapsed());
  labels = signal(STEP_LABELS);
  steps = signal<StepState[]>([]);
  onboardingId!: number;
  clientId!: number;
  showDemoAlert = signal(true);
  showNoPermissionAlert = signal(false);

  hasUpdatePermission = signal(false);
  scopesLoaded = signal(false);

  canSeeStepContent = computed(() => {
    if (this.hasUpdatePermission()) return true;
    if (!this.scopesLoaded()) return false;
    const isMerchantAdmin = this.utilities.roleValue === 'merchant_admin';
    const isDemoClientCow = this.utilities.roleValue === 'demo_client_admin_w_onboarding_wizard';
    const hasMerchantsContractUpdate = this.scopeService.resourceScope('merchants-contract')?.[0]?.actions?.includes('update') ?? false;

    if (!isDemoClientCow && (!isMerchantAdmin || !hasMerchantsContractUpdate)) return false;

    // allowed roles can see steps 1–6; step 7 only after step 6 is completed
    const isStep7 = this.activeKey() === 'fdValidation';
    if (!isStep7) return true;

    const step6Completed = this.steps().find(s => s.step === 'review')?.status === 'completed';
    return step6Completed;
  });

  private destroy$ = new Subject<void>();
  private firstNavDone = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    public utilities: UtilitiesService,
    private onboardingService: OnboardingWizardService,
    private scopeService: ScopeService,
    private cdr: ChangeDetectorRef
  ) {
    this.utilities.updateRouteOnboardingStatus(true);
    this.utilities.fullPageLoading = true;
  }

  ngOnInit(): void {
    this.utilities.showLogo = true;

    const id$ = this.route.paramMap.pipe(
      map(pm => Number(pm.get('onboardingId'))),
      filter(id => !Number.isNaN(id)),
      distinctUntilChanged()
    );

    id$.pipe(takeUntil(this.destroy$)).subscribe(id => {
      this.onboardingId = id;
      this.clientId = Number(this.route.snapshot.paramMap.get('clientId'));
      this.firstNavDone = false;
      this.steps.set([]);
      this.utilities.fullPageLoading = true;
    });

    id$.pipe(
      switchMap(id => {
        this.onboardingId = id;
        this.clientId = Number(this.route.snapshot.paramMap.get('clientId'));
        return this.scopeService.fetchScopes(this.clientId).pipe(
          tap((res: any) => {
            if (res.scopes) {
              this.scopeService.Scope = res.scopes;
              const isUpdate = res.scopes.find((x: any) => x.resource === 'onboarding-wizard' || x.resource === 'client-onboarding')?.actions?.includes('update') ?? false;
              this.hasUpdatePermission.set(isUpdate);
            }
            this.scopesLoaded.set(true);
          }),
          switchMap(() => this.onboardingService.refresh(id).pipe(take(1)))
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe(items => {
      const mapped = normalizeFromApi(items);
      this.steps.set(mapped);
      this.utilities.fullPageLoading = false;

      this.navigateToActive(mapped);
      this.firstNavDone = true;
      this.showNoPermissionAlert.set(!this.hasUpdatePermission());
    });

    id$.pipe(
      switchMap(id =>
        this.router.events.pipe(
          filter((e): e is NavigationEnd => e instanceof NavigationEnd),
          switchMap(() => {
            if (!this.utilities.isFdUserValue && this.router.url.includes('/contract-data')) {
              this.goTo('fdValidation');
            }
            return this.onboardingService.refresh(id);
          })
        )
      ),
      takeUntil(this.destroy$)
    ).subscribe(items => {
      this.steps.set(normalizeFromApi(items));
      this.cdr.markForCheck();
    });
  }

  toggleCollapsed() {
    this.collapsed.update(v => !v);
    try { localStorage.setItem(this.STORAGE_KEY, String(this.collapsed())); } catch { }
  }

  private readCollapsed(): boolean {
    try { return localStorage.getItem(this.STORAGE_KEY) === 'true'; } catch { return false; }
  }

  statusText: Record<StepStatus, string> = {
    pending: 'Pending',
    inProgress: 'In progress',
    completed: 'Completed',
  };

  statusClass(st: StepStatus) {
    return ({
      pending: 'chip--pending',
      inProgress: 'chip--inprogress',
      completed: 'chip--completed',
    } as Record<string, string>)[st];
  }

  isCompleted(st: StepStatus) { return st === 'completed'; }

  readonly routerUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url)
    ),
    { initialValue: this.router.url }
  );

  activeKey = computed<StepKey>(() => {
    const url = this.routerUrl();
    const hits = Object.entries(STEP_PATH)
      .filter(([, p]) => url.includes('/' + p))
      .map(([k]) => k as StepKey);

    if (hits.length === 0) return 'clientDataReview';
    if (hits.includes('clientDataReview')) return 'clientDataReview';
    return hits[hits.length - 1]!;
  });

  selectedIndex = computed(() => {
    const idx = STEP_ORDER.indexOf(this.activeKey());
    return Math.max(0, idx);
  });

  goTo(key: StepKey) {
    if (!this.utilities.isFdUserValue && key === 'contract') {
      key = 'fdValidation';
    }
    const path = STEP_PATH[key];
    this.router.navigate([path], { relativeTo: this.route, replaceUrl: !this.firstNavDone });
  }

  onStepChange(e: StepperSelectionEvent) {
    if (e.selectedIndex === e.previouslySelectedIndex) return;
    const targetKey = STEP_ORDER[e.selectedIndex] as StepKey;
    const target = this.steps().find(s => s.step === targetKey);
    if (!target) return;
    this.goTo(targetKey);
  }

  private navigateToActive(arr: StepState[]) {
    if (!arr?.length) return;
    const existing = STEP_ORDER.filter(k => arr.some(s => s.step === k));

    const allCompleted =
      existing.length > 0 &&
      existing.every(k => arr.find(s => s.step === k)?.status === 'completed');

    if (allCompleted) {
      const last = existing[existing.length - 1] as StepKey;
      return this.goTo(last);
    }

    const inProg = existing.find(k =>
      arr.some(s => s.step === k && s.status === 'inProgress')
    );
    if (inProg) return this.goTo(inProg as StepKey);

    const pending = existing.find(k =>
      arr.some(s => s.step === k && s.status === 'pending')
    );
    if (pending) return this.goTo(pending as StepKey);

    return this.goTo(existing[0] as StepKey);
  }

  hideDemoAlert(): void {
    this.showDemoAlert.set(false);
  }

  hideNoPermissionAlert(): void {
    this.showNoPermissionAlert.set(false);
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
